/* eslint-disable consistent-return */
import React, { FC, useEffect, useMemo } from 'react';

import { Box } from '@mui/system';

import {
  AdminLegendVariant,
  PharmacistLegendVariant,
  ProfessionalReplacementTypes,
  TypeOfBill,
  TypeOfSummary,
  adminActions,
  adminBookedPharmacistDetails,
  adminBookingDetails,
  adminPharmacistResume,
  convertToDecimals,
  getAdminPharmacistResumes,
  userPreferredTimeFormat,
} from '@pharmaplan/common';
import { ReactComponent as Calendar } from '@pharmaplan/common/assets/icons/calendarIconNew.svg';
import {
  idStore,
} from '@pharmaplan/common/helpers/ServiceHandler';
import { getHoursDiff } from '@pharmaplan/common/helpers/utils';
import { Typography } from '@mui/material';
import strings from '../../../../localization';
import ServiceCard from '../../../SelfService/ServiceCard';
import CustomGridLayout from '../../../common/CustomGridLayout';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import {
  Constants,
  ReplacementLabels,
  ScreenTypes,
} from '../../../../helpers/Constants';
import ThemeConstants from '../../../../theme/ThemeConstants';
import { getDayMonthDateYear } from '../../../Reports/helpers';
import { getHourMinuteSeconds } from '../../../../helpers/Functions';
import PharmacistCard from '../../../SelfService/PharmacistCard';
import SoftwareExpertise from '../../../SelfService/SoftwareExpertise';
import PharmacistSummary from '../../../SelfService/PharmacistSummary';
import BillDetails from '../../../SelfService/BillDetails';
import {
  renderScreen,
  setBack,
  setFooterShow,
} from '../../../../actions/drawerActions';

import ProfessionalExperience from '../../../SelfService/PharmacistProfile/ProfessionalExperience/ProfessionalExperience';
import useDetailsPagination from '../../../../hooks/Admin/useDetailsPagination';
import { getMultipleEventAdd } from '../../../../selectors/calendarSelector';
import ViewAllLongTermContracts from '../../../ViewAllLongTermContracts';
import useOptimizedAvatar from '../../../../hooks/useOptimizedAvatar';
import TotalContractValue from '../../../common/TotalContractValue';
import { setDialog } from '../../../../reducers/dialogReducer';
import NoteLabelValue from '../../../common/NoteLabelValue';
import useGetItems from '../../../../hooks/useGetItems';
import CustomButton from '../../../common/CustomButton';
import ServiceNote from '../../../SelfService/ServiceNotes';

import styles from './styles';

interface IViewBookedDetails {
  bookingId: string;
  bookingIdList?: Array<string> | null;
  isExternalPharmacist?: boolean;
  isAutoSyncRecord?: boolean;
}

const {
  getBookedPharmacistDetails,
  adminPharmacistPictures: adminPharmacistPicturesAction,
} = adminActions;

const ViewBookedDetails: FC<IViewBookedDetails> = ({
  bookingId,
  bookingIdList,
  isAutoSyncRecord,
  isExternalPharmacist,
}) => {
  const resume = useAppSelector(adminPharmacistResume);

  const dispatch = useAppDispatch();
  const enabledMultipleAdd = useAppSelector(getMultipleEventAdd);

  const { id } = useDetailsPagination(bookingIdList);
  const { getPharmacistImage } = useGetItems();

  const isNotFromMap = !id;
  const eventId = id ?? bookingId;

  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const event = useAppSelector(adminBookingDetails);
  const {
    experience,
    createDate,
    bookingCost,
    email,
    address,
    notesForSalesOrder,
    notes,
    province,
    softwares,
    startDate,
    postalCode,
    licenseNumber,
    city,
    replacementType,
    longTermShiftContract,
    contractCost,
    professionalBookedCount,
    endDate,
    isProfessionalPromoBooking,
    hourlyRateBonus = 0,
  } = event;

  const hours = getHoursDiff(endDate, startDate) ?? 0;

  const bonus = isProfessionalPromoBooking
    ? convertToDecimals(
      hourlyRateBonus * hours,
    )
    : 0;
  const bonusStr = `${bonus}`;
  const bonusLabel = isProfessionalPromoBooking ? (
    <Typography sx={styles.promoText}>
      {`🔥 ${strings.formatString(strings.xofYApplied, strings.bonus, bonusStr)}`}
    </Typography>
  ) : (
    ''
  );

  const { longTermShiftContractId, allowanceNotes } = longTermShiftContract ?? {};
  const isPharmacist = replacementType === ProfessionalReplacementTypes.pharmacist;

  const userId = event[idStore.id as keyof typeof event] as string;
  const replacement = ReplacementLabels()[
    replacementType ?? ProfessionalReplacementTypes.pharmacist
  ];
  const profilePicture = getPharmacistImage(userId);

  const goToBookingList = () => {
    dispatch(
      renderScreen({
        screenNumber: 1,
        screenType: ScreenTypes.bookingList,
        date: startDate,
      }),
    );
  };

  const goToBookings = () => {
    dispatch(
      setDialog({
        heading: {
          title: strings.bookingDetails,
        },
        Component: (
          <ViewAllLongTermContracts
            type={PharmacistLegendVariant.booked}
            longTermContractId={longTermShiftContractId ?? ''}
            professionalId={event?.[idStore.id as keyof typeof event] as string}
          />
        ),
        maxWidth: 'lg',
        showCloseButton: true,
      }),
    );
  };

  useEffect(() => {
    dispatch(adminBookedPharmacistDetails({ id: eventId, isAutoSyncRecord }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (enabledMultipleAdd) {
      dispatch(setFooterShow(false));
      dispatch(setBack(null));
      return;
    }
    if (isNotFromMap) {
      dispatch(setBack(goToBookingList));
      dispatch(setFooterShow(false));
    }
    return () => {
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  useEffect(() => {
    if (userId) {
      dispatch(getAdminPharmacistResumes(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const pharmacistDetails = [
    { key: strings.yearsOfExperience, value: `${experience}` },
    isPharmacist && { key: strings.licenseNo, value: licenseNumber },
    { key: strings.email, value: email },
    {
      key: strings.address,
      value: `${address}, ${city}, ${province}, ${postalCode}`,
    },
    {
      key: strings.formatString(strings.createdFormat, strings.date) as string,
      value: getDayMonthDateYear(createDate),
      adornments: <Calendar fill={ThemeConstants.common.grey850} />,
    },
    {
      key: strings.formatString(strings.createdFormat, strings.time) as string,
      value: getHourMinuteSeconds(createDate, timeFormat),
    },
  ].filter(Boolean) as Array<{
    key: string;
    value: string;
    adornment: React.ReactNode;
  }>;

  const fullEvent = {
    ...event,
    cost: bookingCost,
  };

  const pIds = useMemo(() =>
    [userId], [userId]);

  useOptimizedAvatar(pIds);

  return (
    <Box sx={styles.container}>
      <PharmacistCard
        eventIds={bookingIdList ?? []}
        eventId={eventId}
        type={PharmacistLegendVariant.booked}
        pharmacistDetails={{
          ...event,
          profilePicture,
        }}
        screenType={ScreenTypes.pharmacistProfile}
        successAction={getBookedPharmacistDetails}
        imageSuccessAction={adminPharmacistPicturesAction}
        customFooterEnd={
          event?.longTermShiftContractId ? (
            <CustomButton
              customButtonStyle={styles.counterOfferBtn}
              customClass={styles.counterOfferContainer}
              onClick={goToBookings}
              label={strings.viewAllBookings}
              variant={Constants.variant.outlined}
            />
          ) : null
        }
        showRating={!isAutoSyncRecord && !isExternalPharmacist}
        isExternalPharmacist={isAutoSyncRecord || isExternalPharmacist}
      />
      <ServiceCard
        customContainerStyle={styles.serviceContainer}
        content={(
          <CustomGridLayout
            successAction={getBookedPharmacistDetails}
            title={strings.professionalDetails}
            config={pharmacistDetails}
          />
        )}
      />

      <ProfessionalExperience
        resume={resume}
        successAction={getBookedPharmacistDetails}
        details={event}
      />

      <SoftwareExpertise
        softwareDetails={softwares}
        successAction={getBookedPharmacistDetails}
      />
      <PharmacistSummary
        event={event}
        summaryType={TypeOfSummary.workshiftDetails}
        type={AdminLegendVariant.requested}
        isAutoSyncRecord={isAutoSyncRecord}
        isLongTerm={!!longTermShiftContractId}
        isPromoBooking={isProfessionalPromoBooking}
      />
      <BillDetails
        EndAdornment={bonusLabel}
        successAction={getBookedPharmacistDetails}
        billType={TypeOfBill.finalBill}
        event={fullEvent}
        customTitle={replacement}
        isAutoSyncRecord={isAutoSyncRecord}
      />

      {!!longTermShiftContractId && (
        <TotalContractValue
          days={professionalBookedCount ?? 0}
          value={contractCost as number}
        />
      )}

      {notes && <ServiceNote notes={notes} />}

      {!!allowanceNotes && (
        <ServiceCard
          customContainerStyle={styles.padTop}
          content={
            <NoteLabelValue label={strings.allowances} value={allowanceNotes} />
          }
        />
      )}

      {notesForSalesOrder && (
        <ServiceNote
          customTitle={strings.notesForSalesOrder}
          notes={notesForSalesOrder}
        />
      )}

    </Box>
  );
};

export default ViewBookedDetails;
